import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { Users, FileText, BarChart3, TrendingUp, Plus, Edit2, Trash2, X, Coins, Check } from "lucide-react";

export function AdminStats() {
    const { t } = useI18n();
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
    if (!stats) return <div className="p-10 text-muted-foreground text-sm">{t.common.loading}</div>;

    const cards = [
        { key: "affiliates_active", label: t.admin.kpi.affiliates, value: stats.affiliates_active, hint: `su ${stats.affiliates_total} totali`, icon: <Users size={16} /> },
        { key: "clients_total", label: t.admin.kpi.clients, value: stats.clients_total, icon: <Users size={16} /> },
        { key: "pratiche_total", label: t.admin.kpi.pratiche, value: stats.pratiche_total, hint: `${stats.pratiche_submitted} inviate`, icon: <FileText size={16} /> },
        { key: "credits_outstanding", label: t.admin.kpi.credits, value: stats.credits_outstanding.toFixed(2), icon: <Coins size={16} /> },
        { key: "revenue_eur", label: t.admin.kpi.revenue, value: `€ ${stats.revenue_eur.toFixed(2)}`, icon: <TrendingUp size={16} /> },
    ];

    return (
        <div className="min-h-screen p-6 lg:p-10">
            <header className="mb-8">
                <h1 className="font-display text-3xl font-medium tracking-tight">{t.admin.stats}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t.admin.statsSubtitle}</p>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cards.map((c) => (
                    <div key={c.key} data-testid={`kpi-${c.key}`} className="p-6 rounded-lg border border-border bg-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-3">
                            {c.icon}<span>{c.label}</span>
                        </div>
                        <div className="font-display text-3xl font-medium">{c.value}</div>
                        {c.hint && <div className="text-xs text-muted-foreground mt-1">{c.hint}</div>}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function AdminAffiliates() {
    const { t } = useI18n();
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | "new" | aff obj
    const [creditAdj, setCreditAdj] = useState(null); // { id, name }

    const load = async () => {
        const r = await api.get("/admin/affiliates");
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Eliminare l'affiliato? L'operazione è irreversibile.")) return;
        await api.delete(`/admin/affiliates/${id}`);
        load();
    };

    return (
        <div className="min-h-screen p-6 lg:p-10">
            <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-medium tracking-tight">{t.admin.affiliates}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t.admin.affiliatesSubtitle}</p>
                </div>
                <button
                    data-testid="new-affiliate-button"
                    onClick={() => setEditing("new")}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
                >
                    <Plus size={16} /> {t.admin.newAffiliate}
                </button>
            </header>

            <div className="rounded-lg border border-border bg-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-background-alt text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-start px-5 py-3 font-semibold">Nome</th>
                            <th className="text-start px-5 py-3 font-semibold">Email</th>
                            <th className="text-end px-5 py-3 font-semibold">Crediti</th>
                            <th className="text-center px-5 py-3 font-semibold">Stato</th>
                            <th className="text-end px-5 py-3 font-semibold">Azioni</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {items.length === 0 && (
                            <tr><td colSpan={5} className="px-5 py-12 text-center text-muted-foreground">Nessun affiliato</td></tr>
                        )}
                        {items.map((a) => (
                            <tr key={a.id} data-testid={`affiliate-row-${a.id}`} className="hover:bg-accent/30 transition-colors">
                                <td className="px-5 py-3 font-medium">{a.full_name}</td>
                                <td className="px-5 py-3 text-muted-foreground">{a.email}</td>
                                <td className="px-5 py-3 text-end font-mono font-semibold">{Number(a.credits_balance || 0).toFixed(2)}</td>
                                <td className="px-5 py-3 text-center">
                                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider ${a.active ? "bg-orange-50 text-orange-800 border border-orange-200" : "bg-orange-50 text-black border border-orange-200"}`}>
                                        {a.active ? t.admin.active : t.admin.disabled}
                                    </span>
                                </td>
                                <td className="px-5 py-3 text-end">
                                    <button data-testid={`credit-${a.id}`} onClick={() => setCreditAdj({ id: a.id, name: a.full_name })} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors" title="Aggiungi crediti">
                                        <Coins size={14} />
                                    </button>
                                    <button data-testid={`edit-affiliate-${a.id}`} onClick={() => setEditing(a)} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                                        <Edit2 size={14} />
                                    </button>
                                    <button data-testid={`delete-affiliate-${a.id}`} onClick={() => del(a.id)} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && <AffiliateDialog aff={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {creditAdj && <CreditDialog target={creditAdj} onClose={() => setCreditAdj(null)} onSaved={() => { setCreditAdj(null); load(); }} />}
        </div>
    );
}

function AffiliateDialog({ aff, onClose, onSaved }) {
    const [data, setData] = useState(aff ? { full_name: aff.full_name, active: aff.active } : { email: "", password: "", full_name: "", initial_credits: 0 });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            if (aff) await api.put(`/admin/affiliates/${aff.id}`, data);
            else await api.post("/admin/affiliates", data);
            onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-orange-900/20 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div data-testid="affiliate-dialog" className="w-full max-w-md bg-card rounded-lg border border-border shadow-xl">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <h2 className="font-display text-lg font-medium">{aff ? "Modifica affiliato" : "Nuovo affiliato"}</h2>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <form onSubmit={save} className="p-6 space-y-4">
                    {!aff && (
                        <>
                            <FieldL label="Email"><input data-testid="aff-email" required type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className={inputCls} /></FieldL>
                            <FieldL label="Password"><input data-testid="aff-password" required minLength={6} type="text" value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} className={inputCls} /></FieldL>
                        </>
                    )}
                    <FieldL label="Nome e cognome"><input data-testid="aff-fullname" required value={data.full_name} onChange={(e) => setData({ ...data, full_name: e.target.value })} className={inputCls} /></FieldL>
                    {!aff && (
                        <FieldL label="Crediti iniziali"><input data-testid="aff-initial-credits" type="number" step="0.01" value={data.initial_credits} onChange={(e) => setData({ ...data, initial_credits: parseFloat(e.target.value || 0) })} className={inputCls} /></FieldL>
                    )}
                    {aff && (
                        <label className="flex items-center gap-2 text-sm">
                            <input data-testid="aff-active" type="checkbox" checked={!!data.active} onChange={(e) => setData({ ...data, active: e.target.checked })} />
                            <span>Attivo</span>
                        </label>
                    )}
                    {err && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{err}</div>}
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md hover:bg-accent text-sm transition-colors">Annulla</button>
                        <button data-testid="save-affiliate" type="submit" disabled={busy} className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors disabled:opacity-50">{busy ? "..." : "Salva"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function CreditDialog({ target, onClose, onSaved }) {
    const [delta, setDelta] = useState(10);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/admin/affiliates/${target.id}/credits`, { delta: parseFloat(delta), note });
            onSaved();
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-orange-900/20 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div data-testid="credit-dialog" className="w-full max-w-sm bg-card rounded-lg border border-border shadow-xl">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <h2 className="font-display text-lg font-medium">Accredita / Sottrai</h2>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <form onSubmit={save} className="p-6 space-y-4">
                    <div className="text-sm text-muted-foreground">Affiliato: <span className="font-semibold text-foreground">{target.name}</span></div>
                    <FieldL label="Variazione crediti (positivo = aggiungi, negativo = sottrai)">
                        <input data-testid="credit-delta" type="number" step="0.01" required value={delta} onChange={(e) => setDelta(e.target.value)} className={inputCls} />
                    </FieldL>
                    <FieldL label="Nota">
                        <input data-testid="credit-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="(opzionale)" />
                    </FieldL>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md hover:bg-accent text-sm transition-colors">Annulla</button>
                        <button data-testid="save-credit-delta" type="submit" disabled={busy} className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors disabled:opacity-50">{busy ? "..." : "Conferma"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export function AdminPricing() {
    return (
        <div className="min-h-screen p-6 lg:p-10">
            <div className="rounded-lg border border-orange-300 bg-white p-5 text-sm text-black">
                Listino generico dismesso per l’MVP: usare Pagamenti → Tariffario P.IVA AdE.
            </div>
        </div>
    );
}


const inputCls = "w-full px-3 py-2 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm";

function FieldL({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-1 block">{label}</span>
            {children}
        </label>
    );
}

export function AdminPayments({ view = "provider" }) {
    const [affiliates, setAffiliates] = useState([]);
    useEffect(() => {
        if (["tariffario"].includes(view)) {
            api.get("/admin/affiliates").then((r) => setAffiliates(r.data || [])).catch(() => setAffiliates([]));
        }
    }, [view]);

    const titleMap = {
        provider: "PAGAMENTI - PROVIDER",
        accrediti: "PAGAMENTI - ACCREDITI AFFILIATI",
        "crediti-generali": "PAGAMENTI - IMPOSTAZIONE CREDITI GENERALE",
        tariffario: "PAGAMENTI - TARIFFARIO",
    };

    return (
        <div className="min-h-screen p-6 lg:p-10">
            <header className="mb-8">
                <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-black">{titleMap[view] || titleMap.provider}</h1>
                <p className="text-sm text-black mt-1">Configurazione amministrativa dei pagamenti, crediti e listini affiliati.</p>
            </header>
            {view === "provider" && <PaymentsProviderPanel />}
            {view === "accrediti" && <AffiliateAccreditsPanel />}
            {view === "crediti-generali" && <CreditGeneralSettingsPanel />}
            {view === "tariffario" && <TariffarioPanel affiliates={affiliates} />}
        </div>
    );
}

function PaymentsProviderPanel() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/payments/admin/provider-settings")
            .then((r) => {
                const rows = Array.isArray(r.data) ? r.data : [];
                setSettings(rows.find((x) => x.provider === "airwallex") || null);
            })
            .catch(() => setSettings(null))
            .finally(() => setLoading(false));
    }, []);

    const configured = !!(settings?.client_id && settings?.api_key_configured && settings?.webhook_secret_configured);
    const webhookUrl = settings?.webhook_url_configured || "https://app.fiscox.ai/api/airwallex/webhook";

    return (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <section className="xl:col-span-2 rounded-lg border border-orange-300 bg-white overflow-hidden">
                <div className="bg-primary px-5 py-3 text-white font-bold uppercase">Impostazione Provider</div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FieldL label="Provider attivo">
                        <select className={inputCls} value={settings?.provider || "airwallex"} disabled>
                            <option value="airwallex">Airwallex</option>
                            <option value="stripe">Stripe</option>
                            <option value="nexi">Nexi / futuro</option>
                            <option value="paypal">PayPal / futuro</option>
                        </select>
                    </FieldL>
                    <FieldL label="Modalità">
                        <select className={inputCls} value={settings?.mode || "test"} disabled>
                            <option value="test">Test</option>
                            <option value="produzione">Produzione</option>
                        </select>
                    </FieldL>
                    <FieldL label="Valuta">
                        <input className={inputCls} defaultValue="EUR" />
                    </FieldL>
                    <FieldL label="Stato configurazione">
                        <input className={inputCls} value={loading ? "CARICAMENTO" : configured ? "CONFIGURATO" : "DA CONFIGURARE"} readOnly />
                    </FieldL>
                    <FieldL label="Client ID">
                        <input className={inputCls} value={settings?.client_id || ""} placeholder="Non configurato" readOnly />
                    </FieldL>
                    <FieldL label="API Key / Secret">
                        <input className={inputCls} value={settings?.api_key_configured ? "Configurata (cifrata)" : "Non configurata"} readOnly />
                    </FieldL>
                    <FieldL label="Webhook Secret">
                        <input className={inputCls} value={settings?.webhook_secret_configured ? "Configurata (cifrata)" : "Non configurata"} readOnly />
                    </FieldL>
                    <FieldL label="URL Webhook">
                        <input className={inputCls} value={webhookUrl} readOnly />
                    </FieldL>
                    <FieldL label="Organization ID">
                        <input className={inputCls} value={settings?.organization_id || ""} placeholder="Non configurato" readOnly />
                    </FieldL>
                    <FieldL label="API Version">
                        <input className={inputCls} value={settings?.api_version || ""} placeholder="Non configurata" readOnly />
                    </FieldL>
                </div>
            </section>
            <section className="rounded-lg border border-orange-300 bg-white overflow-hidden">
                <div className="bg-primary px-5 py-3 text-white font-bold uppercase">Regola tecnica</div>
                <div className="p-5 space-y-3 text-sm text-black">
                    <p>I crediti vengono accreditati solo dopo webhook valido del provider.</p>
                    <p>Il redirect cliente non basta per segnare il pagamento come riuscito.</p>
                    <p>Airwallex rimane un adapter sostituibile: wallet e tariffario restano indipendenti.</p>
                    <p>Le chiavi sensibili non vengono mostrate: restano salvate cifrate nel backend.</p>
                </div>
            </section>
        </div>
    );
}

function AffiliateAccreditsPanel() {
    const transactions = [];
    return (
        <section className="rounded-lg border border-orange-300 bg-white overflow-hidden">
            <div className="bg-primary px-5 py-3 text-white font-bold uppercase">Transazioni di pagamento ricevute dagli affiliati</div>
            <table className="w-full min-w-[980px] text-sm">
                <thead className="bg-primary text-white uppercase text-xs">
                    <tr>
                        <th className="px-4 py-3 text-left font-bold">Data</th>
                        <th className="px-4 py-3 text-left font-bold">Affiliato</th>
                        <th className="px-4 py-3 text-left font-bold">Provider</th>
                        <th className="px-4 py-3 text-right font-bold">Importo</th>
                        <th className="px-4 py-3 text-right font-bold">Crediti acquistati</th>
                        <th className="px-4 py-3 text-center font-bold">Stato</th>
                        <th className="px-4 py-3 text-left font-bold">Riferimento provider</th>
                        <th className="px-4 py-3 text-left font-bold">Webhook</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-orange-200">
                    {transactions.length === 0 && (
                        <tr>
                            <td colSpan={8} className="px-4 py-10 text-center text-black">
                                Nessuna transazione di pagamento ancora registrata. Qui appariranno i pagamenti degli affiliati verso FiscoAI confermati dal provider.
                            </td>
                        </tr>
                    )}
                    {transactions.map((tx) => (
                        <tr key={tx.id}>
                            <td className="px-4 py-3 text-black">{tx.date}</td>
                            <td className="px-4 py-3 font-bold uppercase text-black">{tx.affiliate}</td>
                            <td className="px-4 py-3 text-black">{tx.provider}</td>
                            <td className="px-4 py-3 text-right font-bold text-black">€ {Number(tx.amount || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-right font-bold text-black">{Number(tx.credits || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-center font-bold uppercase text-black">{tx.status}</td>
                            <td className="px-4 py-3 text-black">{tx.providerRef}</td>
                            <td className="px-4 py-3 text-black">{tx.webhook}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}

function CreditGeneralSettingsPanel() {
    return (
        <section className="rounded-lg border border-orange-300 bg-white overflow-hidden max-w-3xl">
            <div className="bg-primary px-5 py-3 text-white font-bold uppercase">Impostazione Crediti Generale</div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldL label="Valore credito base">
                    <input className={inputCls} type="number" step="0.01" defaultValue="1.00" />
                </FieldL>
                <FieldL label="Valuta equivalente">
                    <input className={inputCls} defaultValue="EUR" />
                </FieldL>
                <div className="md:col-span-2 rounded-md border border-orange-300 bg-white p-4 text-sm text-black">
                    Regola di default: <strong>1 credito = 1 euro</strong>. Il tariffario dei moduli usa crediti interni, non importi legati al provider.
                </div>
            </div>
        </section>
    );
}

function TariffarioPanel({ affiliates }) {
    const [pricing, setPricing] = useState([]);
    const [edited, setEdited] = useState({});
    const [moduleSettings, setModuleSettings] = useState({});
    const [saving, setSaving] = useState("");

    const loadPricing = async () => {
        const r = await api.get("/admin/pricing");
        setPricing((r.data || []).filter((x) => x.product_code === "PIVA_ADE"));
        setEdited({});
    };

    useEffect(() => { loadPricing().catch(() => {}); }, []);

    useEffect(() => {
        let cancelled = false;
        const loadSettings = async () => {
            const pairs = await Promise.all((affiliates || []).map(async (a) => {
                try {
                    const r = await api.get(`/admin/affiliates/${a.id}/modules`);
                    const piva = (r.data || []).find((m) => m.product_code === "PIVA_ADE");
                    return [a.id, piva ? !!piva.enabled : true];
                } catch { return [a.id, true]; }
            }));
            if (!cancelled) setModuleSettings(Object.fromEntries(pairs));
        };
        loadSettings();
        return () => { cancelled = true; };
    }, [affiliates]);

    const savePrice = async (item) => {
        setSaving(item.code);
        try {
            const e = edited[item.code] || {};
            await api.put(`/admin/pricing/${item.code}`, {
                price_credits: parseFloat(e.price ?? item.price_credits),
                status: e.status || item.status,
            });
            await loadPricing();
        } finally { setSaving(""); }
    };

    const toggleAffiliate = async (affiliateId, enabled) => {
        setModuleSettings((s) => ({ ...s, [affiliateId]: enabled }));
        await api.put(`/admin/affiliates/${affiliateId}/modules/PIVA_ADE`, { enabled });
    };

    return (
        <div className="space-y-4">
            <section className="rounded-lg border border-orange-300 bg-white overflow-hidden">
                <div className="bg-primary px-5 py-3 text-white font-bold uppercase">Modulo commerciale P.IVA AdE</div>
                <div className="p-5 text-sm text-black space-y-2">
                    <p><strong>PIVA_ADE</strong> è il modulo commerciale unico per AA9/12 e AA7/10 × apertura/variazione/cessazione.</p>
                    <p>La pipeline telematica resta indipendente dentro l’MVP: qui si configurano solo abilitazione modulo e prezzi delle 6 opzioni.</p>
                </div>
            </section>

            <section className="rounded-lg border border-orange-300 bg-white overflow-auto">
                <div className="bg-primary px-5 py-3 text-white font-bold uppercase">Prezzi globali delle 6 opzioni P.IVA AdE</div>
                <table className="w-full min-w-[900px] text-sm">
                    <thead className="bg-primary text-white uppercase text-xs">
                        <tr>
                            <th className="px-4 py-3 text-left font-bold">Opzione</th>
                            <th className="px-4 py-3 text-left font-bold">Modello</th>
                            <th className="px-4 py-3 text-left font-bold">Adempimento</th>
                            <th className="px-4 py-3 text-right font-bold">Crediti</th>
                            <th className="px-4 py-3 text-center font-bold">Stato</th>
                            <th className="px-4 py-3 text-right font-bold">Azioni</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-orange-200">
                        {pricing.map((p) => {
                            const e = edited[p.code] || {};
                            return (
                                <tr key={p.code}>
                                    <td className="px-4 py-3 font-bold text-black">{p.name}</td>
                                    <td className="px-4 py-3 text-black">{p.model_code}</td>
                                    <td className="px-4 py-3 text-black uppercase">{p.adempimento}</td>
                                    <td className="px-4 py-3 text-right">
                                        <input className="w-24 rounded-md border border-orange-300 bg-white px-2 py-1 text-right font-bold text-black" type="number" step="0.01" min="0" defaultValue={p.price_credits} onChange={(ev) => setEdited({ ...edited, [p.code]: { ...e, price: ev.target.value } })} />
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <select className="rounded-md border border-orange-300 bg-white px-2 py-1 text-black" defaultValue={p.status} onChange={(ev) => setEdited({ ...edited, [p.code]: { ...e, status: ev.target.value } })}>
                                            <option value="active">Attivo</option>
                                            <option value="disabled">Disabilitato</option>
                                            <option value="coming_soon">In arrivo</option>
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => savePrice(p)} disabled={saving === p.code} className="px-3 py-1.5 rounded-md bg-primary text-white text-xs font-bold uppercase disabled:opacity-50">{saving === p.code ? "..." : "Salva"}</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>

            <section className="rounded-lg border border-orange-300 bg-white overflow-auto">
                <div className="bg-primary px-5 py-3 text-white font-bold uppercase">Abilitazione PIVA_ADE per affiliato</div>
                <table className="w-full min-w-[700px] text-sm">
                    <thead className="bg-primary text-white uppercase text-xs">
                        <tr>
                            <th className="px-4 py-3 text-left font-bold">Affiliato</th>
                            <th className="px-4 py-3 text-center font-bold">PIVA_ADE attivo</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-orange-200">
                        {(affiliates || []).map((a) => (
                            <tr key={a.id}>
                                <td className="px-4 py-3 font-bold uppercase text-black">{a.full_name}</td>
                                <td className="px-4 py-3 text-center">
                                    <input type="checkbox" checked={moduleSettings[a.id] !== false} onChange={(e) => toggleAffiliate(a.id, e.target.checked)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
